import { supabase } from "@/integrations/supabase/client";

/**
 * Service for getting the current access token.
 * Delegates entirely to supabase-js which manages session refresh automatically.
 * supabase.auth.getSession() reads from an in-memory cache — no network call.
 */
export class TokenService {
  private static readonly MAX_RETRIES = 3;
  private static readonly INITIAL_RETRY_DELAY = 100; // ms

  /**
   * Get the current access token.
   * @param forceRefresh - Force a network token refresh (used for 401 retry)
   * @returns Access token or null if not authenticated
   */
  static async getAccessToken(forceRefresh = false): Promise<string | null> {
    if (forceRefresh) {
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session?.access_token) return null;
      return data.session.access_token;
    }

    return this.getSessionWithRetry();
  }

  /**
   * The current access token if a session exists right now, else null — no
   * retry, no backoff.
   *
   * For public-lane calls a missing session is the normal state: the caller is
   * an anonymous visitor on a share link, a QR code or a support page. The
   * backoff in `getAccessToken` exists to ride out session hydration for a
   * signed-in user; applied to an anonymous one it is 700 ms of sleep before
   * every request. A signed-in user whose session is still hydrating simply
   * makes this one call anonymously, which the public lane serves anyway.
   */
  static async getAccessTokenIfSignedIn(): Promise<string | null> {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    return !error && session?.access_token ? session.access_token : null;
  }

  private static async getSessionWithRetry(
    attempt = 0
  ): Promise<string | null> {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    const hasToken = !error && session?.access_token;

    if (hasToken) {
      return session.access_token;
    }

    if (attempt < this.MAX_RETRIES) {
      const delay = this.INITIAL_RETRY_DELAY * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return this.getSessionWithRetry(attempt + 1);
    }

    return null;
  }

  /**
   * No-op kept for call-site compatibility.
   * supabase-js manages session state internally.
   */
  static invalidateCache(): void {
    // intentionally empty — supabase-js owns session state
  }
}
